package com.nonopn.skywatcher.common.app.pages.map.view

import androidx.compose.foundation.Image
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.Icon
import androidx.compose.material.Surface
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skywatcher.common.app.home.HotPreviewApp
import com.nonopn.skywatcher.common.app.home.LocalApp
import com.nonopn.skywatcher.common.app.pages.map.model.FakeMainScreenModel
import com.nonopn.skywatcher.common.app.pages.map.model.MainScreenModel
import com.nonopn.skywatcher.common.app.pages.map.model.drawable
import com.nonopn.skywatcher.common.app.pages.map.model.forecast.ForecastLine
import com.nonopn.skywatcher.common.app.pages.map.model.forecast.GraphColorsType
import com.nonopn.skywatcher.common.app.pages.map.model.icon
import com.nonopn.skywatcher.common.app.pages.map.model.weather.ProbeStatus
import com.nonopn.skywatcher.common.app.pages.map.model.weather.WeatherCoded
import com.nonopn.skywatcher.common.app.pages.map.model.weather.WeatherReport
import com.nonopn.skywatcher.common.app.pages.map.view.left.DateSection
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.aqi_index
import com.nonopn.skywatcher.common.app.res.uv_index
import com.nonopn.skywatcher.design.local.LocalFontSizes
import com.nonopn.skywatcher.design.theme.LocalApplicationColorTheme
import com.nonopn.skywatcher.design.utils.localized
import com.nonopn.skywatcher.design.widgets.DefaultCard
import com.nonopn.skywatcher.design.widgets.defaultCardBackground
import com.nonopn.skywatcher.design.widgets.defaultScrollBackground
import com.nonopn.skywatcher.design.widgets.progress.SimpleProgressIndicator
import com.nonopn.skywatcher.design.widgets.verticalColumnScrollbar
import compose.icons.WeatherIcons
import compose.icons.weathericons.DirectionRight
import de.drick.compose.hotpreview.HotPreview
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.weather.common.compose.stringResourceExplanation
import org.jetbrains.compose.resources.painterResource
import kotlin.math.max

@Composable
fun MainColumn(
    modifier: Modifier,
    model: MainScreenModel,
    paddingBottom: Dp = 0.dp,
    header: @Composable () -> Unit = { /* nothing */ }
) {
    val state by model.states.collectAsState()
    val weatherReport = state.probeStatus?.report

    if (state.updatingCoordinates) {
        MainColumnForecastLoading(
            modifier,
            model
        )
        return
    }

    if (null == weatherReport) {
        MainColumnForecastError(
            modifier,
            model
        )
        return
    }

    MainColumnForecaseLoaded(
        modifier,
        paddingBottom,
        model,
        weatherReport,
        header
    )
}

@Composable
private fun MainColumnForecastLoading(
    modifier: Modifier,
    model: MainScreenModel
) {
    MainColumnForecastMessage(modifier, model, "Loading weather data", false)
}

@Composable
private fun MainColumnForecastError(
    modifier: Modifier,
    model: MainScreenModel
) {
    MainColumnForecastMessage(modifier, model, "Error loading forecast data", true)
}

@Composable
private fun MainColumnForecastMessage(
    modifier: Modifier,
    model: MainScreenModel,
    message: String,
    canReload: Boolean
) {
    val color = defaultCardBackground()
    val colors = LocalApplicationColorTheme.current

    DefaultCard(
        modifier,
        backgroundColor = color,
        columnModifier = Modifier.padding(12.dp)
    ) {
        Column(
            Modifier.weight(1.0f).fillMaxWidth(),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            TextNormal(
                message,
                color = colors.fonts.graySemiTransparent,
                fontSize = LocalFontSizes.current.weatherColumn.disclaimer
            )
        }

        Separator()

        TextNormal(
            "Sky Sentinel Admin internal build dev. Internal Usage Only",
            color = colors.fonts.graySemiTransparent,
            fontStyle = FontStyle.Italic,
            fontSize = LocalFontSizes.current.weatherColumn.disclaimer
        )
    }
}

@Composable
private fun MainColumnForecaseLoaded(
    modifier: Modifier,
    paddingBottom: Dp,
    model: MainScreenModel,
    report: WeatherReport,
    header: (@Composable () -> Unit)? = null
) {
    val color = defaultCardBackground()
    val colors = LocalApplicationColorTheme.current

    val state by model.states.collectAsState()

    val weatherFontSizes = LocalFontSizes.current.weatherColumn

    val horizontalPadding = 12.dp
    val verticalPadding = 8.dp

    DefaultCard(
        modifier,
        backgroundColor = color,
        columnModifier = Modifier
    ) {
        val scrollState = rememberScrollState()
        val scrollBackground = defaultScrollBackground()
        Column(
            Modifier.weight(1.0f).verticalScroll(scrollState).verticalColumnScrollbar(
                scrollState = scrollState,
                scrollBarColor = scrollBackground
            )
        ) {
            header?.invoke()

            DateSection(Modifier.fillMaxWidth().padding(horizontal = horizontalPadding))

            val weatherDecoded = WeatherCoded(report.weatherCoded).weather

            weatherDecoded?.let { condition ->
                Surface(elevation = 12.dp) {
                    Image(
                        modifier = Modifier.fillMaxWidth().height(120.dp),
                        painter = painterResource(condition.drawable()),
                        contentScale = ContentScale.Crop,
                        contentDescription = ""
                    )
                }

                Column(
                    Modifier.padding(
                        vertical = verticalPadding,
                        horizontal = horizontalPadding
                    )
                ) {
                    Column {
                        val (title, explanation) = condition.stringResourceExplanation

                        Row(
                            Modifier,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                modifier = Modifier.width(50.dp).height(50.dp),
                                imageVector = condition.icon,
                                contentDescription = "",
                                tint = colors.fonts.regular
                            )

                            Column {
                                TextNormal(
                                    title.localized(),
                                    fontSize = weatherFontSizes.conditionTitle
                                )

                                explanation?.let { text ->
                                    TextNormal(
                                        text.localized(),
                                        fontSize = weatherFontSizes.conditionText,
                                        color = colors.fonts.regularLighter
                                    )
                                }
                            }
                        }

                        /*
                        TextNormal(
                            "Debug WeatherCoded : ${state.probeStatus?.report?.weatherCoded}",
                            fontStyle = FontStyle.Italic,
                            fontSize = weatherFontSizes.disclaimer,
                            color = colors.fonts.graySemiTransparent
                        )
                         */
                    }
                }
            }

            Column(
                Modifier.fillMaxWidth().padding(horizontal = horizontalPadding)
            ) {
                Separator(top = 0.dp)

                listOfNotNull(
                    Triple(
                        Res.string.uv_index,
                        report.actualUVIndex,
                        max(report.uvi, 1) * 1.0f / 12
                    ),
                    report.actualAQI?.let {
                        Triple(
                            Res.string.aqi_index,
                            it,
                            (500 - max(report.aqi ?: 0, 1) * 1.0f) / 500
                        )
                    }
                ).forEach { (text, index, value) ->
                    Column(
                        Modifier.fillMaxWidth().padding(bottom = 8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        TextNormal(
                            text = text.localized(),
                            fontSize = weatherFontSizes.indexTitleBeforeCard,
                            color = colors.fonts.regular
                        )

                        DefaultCard(
                            modifier = Modifier.width(80.dp).height(25.dp),
                            backgroundColor = index.color,
                        ) {
                            Column(
                                Modifier.fillMaxSize(),
                                verticalArrangement = Arrangement.Center,
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                TextNormal(
                                    index.text.localized(),
                                    fontSize = weatherFontSizes.indexTextInsideCard,
                                    textAlign = TextAlign.Center,
                                    color = index.textColor
                                )
                            }
                        }

                        Row(Modifier.padding(horizontal = 8.dp)) {
                            SimpleProgressIndicator(
                                modifier = Modifier.fillMaxWidth()
                                    .height(4.dp),
                                progress = value,
                                progressBarColor = index.color,
                                trackColor = index.colorSub,
                                cornerRadius = 2.dp
                            )
                        }
                    }
                }

                Separator(top = 4.dp, bottom = 4.dp)

                state.aggregator?.forecastLines?.forEach {
                    DrawForecastLine(it)
                }

                if (state.showPollutants && report.airQualityPollutants.isNotEmpty()) {
                    report.airQualityPollutants.forEach {
                        Column(
                            Modifier.fillMaxWidth().padding(bottom = 16.dp),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            TextNormal(
                                "${it.name.capitalize()}",
                                fontSize = 16.sp,
                                color = colors.fonts.regular
                            )

                            it.actualAQI.let { aqi ->
                                DefaultCard(
                                    modifier = Modifier.width(50.dp).height(25.dp),
                                    backgroundColor = aqi.color,
                                ) {
                                    Column(
                                        Modifier.fillMaxSize(),
                                        verticalArrangement = Arrangement.Center,
                                        horizontalAlignment = Alignment.CenterHorizontally
                                    ) {
                                        TextNormal(
                                            "${it.aqi}",
                                            fontSize = 16.sp,
                                            color = aqi.textColor
                                        )
                                    }
                                }
                            }

                            Row(
                                horizontalArrangement = Arrangement.spacedBy(16.dp)
                            ) {
                                it.valuePPB?.let { valuePPB ->
                                    TextNormal(
                                        "${valuePPB}ppb",
                                        fontSize = 12.sp,
                                        color = colors.fonts.regular
                                    )
                                }

                                it.valueUGM3?.let { valueUGM3 ->
                                    TextNormal(
                                        "${valueUGM3}µg/m3",
                                        fontSize = 12.sp,
                                        color = colors.fonts.regular
                                    )
                                }
                            }
                        }
                    }
                }
            }

            Column(Modifier.weight(1.0f)) { }
        }

        Column(Modifier) {
            Separator(top = 0.dp)
            /*
            Row(
                Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.End
            ) {
                Icon(
                    modifier = Modifier.width(28.dp).height(28.dp),
                    imageVector = Icons.Default.Settings,
                    contentDescription = "",
                    tint = colors.fonts.regular
                )
            }

            Separator()
            */

            TextNormal(
                "Sky Sentinel Admin internal build dev. Internal Usage Only",
                fontStyle = FontStyle.Italic,
                fontSize = weatherFontSizes.disclaimer,
                color = colors.fonts.graySemiTransparent
            )
        }

        Column(Modifier.width(1.dp).height(paddingBottom)) { }
    }
}

@Composable
private fun DrawForecastLine(line: ForecastLine) {
    val colors = LocalApplicationColorTheme.current
    val weathers = colors.weather
    val sizes = LocalFontSizes.current.weatherColumn

    Column {
        Row(
            modifier = Modifier.padding(bottom = 4.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (null != line.icon) {
                Icon(
                    modifier = Modifier.width(30.dp).height(30.dp),
                    painter = rememberVectorPainter(line.icon),
                    contentDescription = "",
                    tint = colors.fonts.regular
                )
            } else {
                Column(
                    Modifier.width(30.dp).height(30.dp)
                ) { }
            }

            val value = when (line) {
                is ForecastLine.String -> line.data
                is ForecastLine.Double -> "${line.data}"
                is ForecastLine.Float -> "${line.data}"
                is ForecastLine.Int -> "${line.data}"
            }

            val next = if (line.hasNext()) when (line) {
                is ForecastLine.String -> line.next
                is ForecastLine.Double -> "${line.next}"
                is ForecastLine.Float -> "${line.next}"
                is ForecastLine.Int -> "${line.next}"
            } else null

            Row(
                Modifier.weight(1.0f),
                verticalAlignment = Alignment.CenterVertically
            ) {
                TextNormal(
                    modifier = Modifier.padding(start = 8.dp),
                    text = "$value${line.suffix}",
                    fontSize = sizes.infoMainNow,
                    color = colors.fonts.regular
                )
            }

            next?.let {
                Row(
                    Modifier.weight(1.3f),
                    horizontalArrangement = Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        imageVector = WeatherIcons.DirectionRight,
                        contentDescription = "",
                        tint = colors.fonts.regular
                    )

                    Row(
                        Modifier.weight(1.0f),
                        horizontalArrangement = Arrangement.End
                    ) {

                        TextNormal(
                            modifier = Modifier.padding(start = 8.dp),
                            text = "$next${line.suffix}",
                            fontSize = sizes.infoMainLater,
                            color = colors.fonts.regularLighter
                        )
                    }
                }
            }
        }

        line.graphValue?.let {
            val color = when (it.type) {
                GraphColorsType.Rain ->
                    weathers.rainProbabilityMain to weathers.rainProbabilitySecondary

                GraphColorsType.Humidity ->
                    weathers.rainProbabilityMain to weathers.rainProbabilitySecondary // for now keep the rain one
            }
            Row(Modifier.padding(horizontal = 8.dp)) {
                SimpleProgressIndicator(
                    modifier = Modifier.fillMaxWidth()
                        .height(4.dp),
                    progress = it.float,
                    progressBarColor = color.first,
                    trackColor = color.second,
                    cornerRadius = 2.dp
                )
            }
        }

        Column(Modifier.height(8.dp)) { }
    }
}

@Composable
private fun Separator(top: Dp = 8.dp, bottom: Dp = 8.dp) {
    Surface(
        Modifier.padding(top = top, bottom = bottom),
        elevation = 3.dp
    ) {
        HorizontalDivider(Modifier.height(1.dp))
    }
}

@HotPreview(widthDp = 250, heightDp = 800, darkMode = true)
@HotPreview(widthDp = 250, heightDp = 800, darkMode = false)
@Composable
private fun MainColumnPreview() {
    HotPreviewApp(Modifier.fillMaxSize(), isDarkTheme = isSystemInDarkTheme(), isPreview = true) {
        MainColumn(
            Modifier.fillMaxSize(),
            FakeMainScreenModel(
                false,
                fakeProbeStatus = ProbeStatus(
                    report = WeatherReport.fake()
                )
            )
        )
    }
}

@HotPreview(widthDp = 250, heightDp = 400, darkMode = true)
@HotPreview(widthDp = 250, heightDp = 400, darkMode = false)
@Composable
private fun MainColumnNoForecastPrev() {
    HotPreviewApp(Modifier.fillMaxSize(), isDarkTheme = isSystemInDarkTheme(), isPreview = true) {
        MainColumn(Modifier.fillMaxSize(), FakeMainScreenModel(false, null))
    }
}

@HotPreview(widthDp = 250, heightDp = 400, darkMode = true)
@HotPreview(widthDp = 250, heightDp = 400, darkMode = false)
@Composable
private fun MainColumnLoadingForecastPrev() {
    HotPreviewApp(Modifier.fillMaxSize(), isDarkTheme = isSystemInDarkTheme(), isPreview = true) {
        MainColumn(
            Modifier.fillMaxSize(),
            FakeMainScreenModel(
                true,
                null
            )
        )
    }
}

